import { useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@mui/material'

export const COMPLETE_ORDER_CONFIRM_PHRASE = 'Complete Order'

export const COMPLETE_ORDER_PRESET_REASONS = [
  'Out of Stock',
  'Product Already Received from Outlet',
  "Grocer Doesn't Need the Leftover Products",
  'Other',
]

export const COMPLETE_ORDER_OTHER_REASON = 'Other'

/** @returns {string} Trimmed reason sent to the complete-order API. */
export function resolveCompleteOrderReason({
  isPartialDelivery,
  presetReason,
  otherReasonText,
  freeTextReason,
}) {
  if (isPartialDelivery) {
    if (!presetReason) return ''
    if (presetReason === COMPLETE_ORDER_OTHER_REASON) {
      return otherReasonText.trim()
    }
    return presetReason
  }
  return freeTextReason.trim()
}

/**
 * Confirmation dialog for admin Complete: explains effects, requires reason,
 * and requires typing "Complete Order".
 */
const CompleteOrderDialog = ({
  open,
  onClose,
  onConfirm,
  loading = false,
  error = null,
  onClearError,
  isPartialDelivery = false,
}) => {
  const [freeTextReason, setFreeTextReason] = useState('')
  const [presetReason, setPresetReason] = useState('')
  const [otherReasonText, setOtherReasonText] = useState('')
  const [confirmText, setConfirmText] = useState('')

  useEffect(() => {
    if (!open) {
      setFreeTextReason('')
      setPresetReason('')
      setOtherReasonText('')
      setConfirmText('')
    }
  }, [open])

  const resolvedReason = useMemo(
    () =>
      resolveCompleteOrderReason({
        isPartialDelivery,
        presetReason,
        otherReasonText,
        freeTextReason,
      }),
    [isPartialDelivery, presetReason, otherReasonText, freeTextReason],
  )

  const reasonOk = resolvedReason.length > 0
  const phraseOk = confirmText === COMPLETE_ORDER_CONFIRM_PHRASE
  const canSubmit = reasonOk && phraseOk && !loading

  const presetTouched = isPartialDelivery && presetReason !== ''
  const otherNeedsText =
    isPartialDelivery && presetReason === COMPLETE_ORDER_OTHER_REASON

  const handleConfirm = () => {
    if (!canSubmit) return
    onConfirm?.(resolvedReason)
  }

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, bgcolor: '#f8f9fb' }}>
        Complete Order
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {error && (
          <Alert
            severity="error"
            onClose={onClearError}
            sx={{ mb: 2 }}
          >
            {error}
          </Alert>
        )}
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          Completing this order will:
        </Typography>
        <Box component="ul" sx={{ mt: 0, mb: 2, pl: 2.5 }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            {isPartialDelivery ? (
              <>
                Move the order to{' '}
                <strong>Closed with Partial Delivery</strong> and lock it.
              </>
            ) : (
              <>
                Move the order to <strong>Completed</strong> and lock it.
              </>
            )}
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
            Auto-confirm any pending shipments as fully received.
          </Typography>
          <Typography component="li" variant="body2">
            Remove undelivered items — quantity that was never received or
            returned will be taken off the order and will{' '}
            <strong>not</strong> be added to the grocer&apos;s stock. Fully
            returned lines stay on the order for history. Only quantities
            already received are kept in inventory.
          </Typography>
        </Box>

        {isPartialDelivery ? (
          <FormControl
            component="fieldset"
            fullWidth
            required
            error={presetTouched && !reasonOk}
            sx={{ mb: 1 }}
          >
            <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
              Reason for closing with partial delivery
            </Typography>
            <RadioGroup
              value={presetReason}
              onChange={(e) => setPresetReason(e.target.value)}
            >
              {COMPLETE_ORDER_PRESET_REASONS.map((label) => (
                <FormControlLabel
                  key={label}
                  value={label}
                  control={<Radio size="small" disabled={loading} />}
                  label={label}
                  disabled={loading}
                />
              ))}
            </RadioGroup>
            {otherNeedsText && (
              <TextField
                label="Please specify"
                value={otherReasonText}
                onChange={(e) => setOtherReasonText(e.target.value)}
                fullWidth
                required
                multiline
                minRows={2}
                disabled={loading}
                error={otherReasonText.length > 0 && !otherReasonText.trim()}
                helperText={
                  !otherReasonText.trim()
                    ? 'A custom reason is required when Other is selected'
                    : ' '
                }
                sx={{ mt: 1 }}
              />
            )}
            <FormHelperText>
              {!presetReason
                ? 'Select a reason to continue'
                : otherNeedsText && !otherReasonText.trim()
                  ? 'Enter a custom reason'
                  : ' '}
            </FormHelperText>
          </FormControl>
        ) : (
          <TextField
            label="Reason for completion"
            value={freeTextReason}
            onChange={(e) => setFreeTextReason(e.target.value)}
            fullWidth
            required
            multiline
            minRows={2}
            disabled={loading}
            error={freeTextReason.length > 0 && !reasonOk}
            helperText={!reasonOk ? 'Reason is required' : ' '}
            sx={{ mb: 1 }}
          />
        )}

        <Typography variant="body2" sx={{ mb: 1, fontWeight: 500 }}>
          Type <strong>{COMPLETE_ORDER_CONFIRM_PHRASE}</strong> to confirm:
        </Typography>
        <TextField
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          fullWidth
          size="small"
          disabled={loading}
          placeholder={COMPLETE_ORDER_CONFIRM_PHRASE}
          error={confirmText.length > 0 && !phraseOk}
        />
      </DialogContent>
      <DialogActions sx={{ p: 2, bgcolor: '#f8f9fb' }}>
        <Button onClick={onClose} disabled={loading} color="inherit">
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="success"
          disabled={!canSubmit}
          startIcon={loading ? <CircularProgress size={16} color="inherit" /> : null}
        >
          {loading ? 'Completing…' : 'Complete Order'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default CompleteOrderDialog
